import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  Phone,
  Mail,
  Building2,
  Calendar,
  MoreHorizontal,
  UserPlus,
  Download,
  SlidersHorizontal,
  ArrowUpDown,
  Check,
  Link2,
  Clock
} from 'lucide-react';
import CreateContactModal from '@/components/CreateContactModal';
import ViewContactModal from '@/components/ViewContactModal';
import { useToastNotification } from '@/contexts/ToastContext';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Placeholder contact data (fallback)
const placeholderContacts = [
  {
    id: '1',
    firstName: 'Sarah',
    lastName: 'Chen',
    email: 'sarah.chen@techventures.com',
    phone: '+1 (415) 555-0123',
    company: 'Tech Ventures Capital',
    role: 'Managing Partner',
    status: 'active',
    type: 'investor',
    lastContact: '2025-01-10',
    dealSize: '$5M - $10M',
    location: 'San Francisco, CA',
    tags: ['Series A', 'Tech Focus']
  },
  {
    id: '2',
    firstName: 'Michael',
    lastName: 'Roberts',
    email: 'm.roberts@blueocean.fund',
    phone: '+1 (212) 555-0456',
    company: 'Blue Ocean Fund',
    role: 'Investment Director',
    status: 'active',
    type: 'investor',
    lastContact: '2025-01-08',
    dealSize: '$10M - $25M',
    location: 'New York, NY',
    tags: ['Growth Stage', 'Healthcare']
  },
  {
    id: '3',
    firstName: 'Emily',
    lastName: 'Watson',
    email: 'emily.w@greenfield.vc',
    phone: '+1 (617) 555-0789',
    company: 'Greenfield Ventures',
    role: 'Principal',
    status: 'pending',
    type: 'investor',
    lastContact: '2025-01-05',
    dealSize: '$2M - $5M',
    location: 'Boston, MA',
    tags: ['Seed', 'CleanTech']
  },
  {
    id: '4',
    firstName: 'David',
    lastName: 'Kim',
    email: 'd.kim@horizoncap.com',
    phone: '+1 (310) 555-0321',
    company: 'Horizon Capital',
    role: 'Partner',
    status: 'active',
    type: 'investor',
    lastContact: '2025-01-12',
    dealSize: '$25M+',
    location: 'Los Angeles, CA',
    tags: ['Late Stage', 'Media']
  },
  {
    id: '5',
    firstName: 'Jennifer',
    lastName: 'Martinez',
    email: 'j.martinez@alphagroup.io',
    phone: '+1 (512) 555-0654',
    company: 'Alpha Group',
    role: 'CEO',
    status: 'inactive',
    type: 'portfolio',
    lastContact: '2024-12-20',
    dealSize: '$1M - $2M',
    location: 'Austin, TX',
    tags: ['SaaS', 'B2B']
  },
  {
    id: '6',
    firstName: 'James',
    lastName: 'Thompson',
    email: 'james.t@summit.partners',
    phone: '+44 20 7555 0123',
    company: 'Summit Partners',
    role: 'Senior Associate',
    status: 'active',
    type: 'investor',
    lastContact: '2025-01-11',
    dealSize: '$5M - $10M',
    location: 'London, UK',
    tags: ['Series B', 'FinTech']
  },
  {
    id: '7',
    firstName: 'Lisa',
    lastName: 'Anderson',
    email: 'lisa.a@redwood.vc',
    phone: '+1 (650) 555-0987',
    company: 'Redwood Ventures',
    role: 'Venture Partner',
    status: 'active',
    type: 'investor',
    lastContact: '2025-01-09',
    dealSize: '$10M - $25M',
    location: 'Palo Alto, CA',
    tags: ['AI/ML', 'Deep Tech']
  },
  {
    id: '8',
    firstName: 'Robert',
    lastName: 'Garcia',
    email: 'r.garcia@nexusventures.com',
    phone: '+1 (305) 555-0246',
    company: 'Nexus Ventures',
    role: 'Managing Director',
    status: 'pending',
    type: 'investor',
    lastContact: '2025-01-06',
    dealSize: '$2M - $5M',
    location: 'Miami, FL',
    tags: ['Series A', 'Consumer']
  },
  {
    id: '9',
    firstName: 'Amanda',
    lastName: 'Lee',
    email: 'amanda.lee@innovatefund.com',
    phone: '+1 (206) 555-0135',
    company: 'Innovate Fund',
    role: 'Partner',
    status: 'active',
    type: 'investor',
    lastContact: '2025-01-13',
    dealSize: '$5M - $10M',
    location: 'Seattle, WA',
    tags: ['Growth Stage', 'Enterprise']
  },
  {
    id: '10',
    firstName: 'Christopher',
    lastName: 'Brown',
    email: 'c.brown@vanguardcap.io',
    phone: '+1 (312) 555-0864',
    company: 'Vanguard Capital',
    role: 'Investment Manager',
    status: 'inactive',
    type: 'investor',
    lastContact: '2024-11-15',
    dealSize: '$1M - $2M',
    location: 'Chicago, IL',
    tags: ['Seed', 'PropTech']
  },
  {
    id: '11',
    firstName: 'Rachel',
    lastName: 'Nguyen',
    email: 'rachel.n@pacificvc.com',
    phone: '+1 (408) 555-0753',
    company: 'Pacific Ventures',
    role: 'Principal',
    status: 'active',
    type: 'investor',
    lastContact: '2025-01-07',
    dealSize: '$10M - $25M',
    location: 'San Jose, CA',
    tags: ['Series B', 'Hardware']
  },
  {
    id: '12',
    firstName: 'Daniel',
    lastName: 'Wilson',
    email: 'd.wilson@capitalbridge.com',
    phone: '+1 (202) 555-0951',
    company: 'Capital Bridge',
    role: 'Associate',
    status: 'pending',
    type: 'investor',
    lastContact: '2025-01-04',
    dealSize: '$2M - $5M',
    location: 'Washington, DC',
    tags: ['GovTech', 'Impact']
  }
];

// Status badge component
const StatusBadge = ({ status }) => {
  const statusStyles = {
    active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    inactive: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusStyles[status]}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// Type badge component
const TypeBadge = ({ type }) => {
  const typeStyles = {
    investor: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    portfolio: 'bg-purple-500/10 text-purple-400 border-purple-500/20'
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${typeStyles[type]}`}>
      {type.charAt(0).toUpperCase() + type.slice(1)}
    </span>
  );
};

const AllContacts = () => {
  const toast = useToastNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedRowRef = useRef(null);
  
  // State for contacts data
  const [contacts, setContacts] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for highlighting
  const [highlightedContactId, setHighlightedContactId] = useState(null);
  
  // State for modals
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [viewingContact, setViewingContact] = useState(null);
  
  // State for organization linking
  const [linkingContactId, setLinkingContactId] = useState(null);
  const [orgSearchOpen, setOrgSearchOpen] = useState(false);
  
  // State for filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dealSizeFilter, setDealSizeFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'lastName', direction: 'asc' });

  // Helper function to show toast (uses global context)
  const showToast = (type, message, addonText) => {
    toast[type === 'gray' ? 'gray' : type](message, addonText);
  };

  // Handle highlight and search params from URL
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    const searchFromUrl = searchParams.get('search');
    
    if (highlightId) {
      setHighlightedContactId(highlightId);
      // Clear the highlight after 2 seconds
      const timer = setTimeout(() => {
        setHighlightedContactId(null);
        // Remove highlight param from URL
        searchParams.delete('highlight');
        setSearchParams(searchParams, { replace: true });
      }, 2000);
      return () => clearTimeout(timer);
    }
    
    if (searchFromUrl) {
      setSearchQuery(searchFromUrl);
      // Remove search param from URL
      searchParams.delete('search');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Scroll to highlighted contact when data loads
  useEffect(() => {
    if (highlightedContactId && highlightedRowRef.current && !isLoading) {
      highlightedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedContactId, isLoading, contacts]);

  // Fetch contacts and organizations on mount
  useEffect(() => {
    fetchContacts();
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/organizations`, {
        withCredentials: true
      });
      // Filter to only active organizations
      setOrganizations(response.data.filter(org => org.status === 'active'));
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  };

  const fetchContacts = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/api/contacts`, {
        withCredentials: true
      });
      setContacts(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Failed to load contacts');
      // Fallback to placeholder data
      setContacts(placeholderContacts);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateContact = async (contactData, contactId) => {
    try {
      if (contactId) {
        // Update existing contact
        const response = await axios.patch(
          `${API_URL}/api/contacts/${contactId}`,
          contactData,
          { withCredentials: true }
        );
        setContacts(prev => prev.map(c => c.id === contactId ? response.data : c));
        
        // Update viewing contact if it's the same one being edited
        if (viewingContact && viewingContact.id === contactId) {
          setViewingContact(response.data);
        }
      } else {
        // Create new contact
        const response = await axios.post(
          `${API_URL}/api/contacts`,
          contactData,
          { withCredentials: true }
        );
        setContacts(prev => [...prev, response.data]);
      }
      setEditingContact(null);
    } catch (err) {
      console.error('Error saving contact:', err);
      throw err;
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/contacts/${contactId}`, {
        withCredentials: true
      });
      setContacts(prev => prev.filter(c => c.id !== contactId));
      showToast('success', 'Contact deleted successfully', 'Success');
    } catch (err) {
      console.error('Error deleting contact:', err);
      showToast('error', 'Failed to delete contact', 'Error');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedContacts.length} contact(s)?`)) return;
    
    try {
      await axios.post(
        `${API_URL}/api/contacts/bulk-delete`,
        selectedContacts,
        { withCredentials: true }
      );
      setContacts(prev => prev.filter(c => !selectedContacts.includes(c.id)));
      setSelectedContacts([]);
      showToast('success', `${selectedContacts.length} contact(s) deleted`, 'Success');
    } catch (err) {
      console.error('Error deleting contacts:', err);
      showToast('error', 'Failed to delete contacts', 'Error');
    }
  };

  const handleEditContact = (contact, editContext = 'contact') => {
    setEditingContact({ ...contact, editContext });
    setIsEditModalOpen(true);
  };

  const handleViewContact = (contact) => {
    setViewingContact(contact);
    setIsViewModalOpen(true);
  };

  const handleAddNewContact = () => {
    setEditingContact(null);
    setIsEditModalOpen(true);
  };

  // Handle linking contact to organization
  const handleLinkOrganization = async (contactId, organizationId) => {
    try {
      const response = await axios.patch(
        `${API_URL}/api/contacts/${contactId}`,
        { organizationId: organizationId || '' },
        { withCredentials: true }
      );
      setContacts(prev => prev.map(c => c.id === contactId ? response.data : c));
      setLinkingContactId(null);
      setOrgSearchOpen(false);
      showToast('success', organizationId ? 'Organization linked' : 'Organization unlinked', 'Success');
    } catch (err) {
      console.error('Error linking organization:', err);
      showToast('error', 'Failed to link organization', 'Error');
    }
  };

  // Handle updating last contact time to now (UTC)
  const handleUpdateLastContact = async (contactId) => {
    try {
      const nowUTC = new Date().toISOString();
      const response = await axios.patch(
        `${API_URL}/api/contacts/${contactId}`,
        { lastContact: nowUTC },
        { withCredentials: true }
      );
      setContacts(prev => prev.map(c => c.id === contactId ? response.data : c));
      showToast('success', 'Last contact updated', 'Success');
    } catch (err) {
      console.error('Error updating last contact:', err);
      showToast('error', 'Failed to update last contact', 'Error');
    }
  };

  // Handle clearing last contact time (Cmd/Ctrl+Click)
  const handleClearLastContact = async (contactId) => {
    try {
      const response = await axios.patch(
        `${API_URL}/api/contacts/${contactId}`,
        { lastContact: '' },
        { withCredentials: true }
      );
      setContacts(prev => prev.map(c => c.id === contactId ? response.data : c));
      showToast('gray', 'Last contact cleared', 'Info');
    } catch (err) {
      console.error('Error clearing last contact:', err);
      showToast('error', 'Failed to clear last contact', 'Error');
    }
  };

  // Get unique values for filter options - Deal Size Ranges
  const dealSizeRanges = [
    { label: '$10K - $20K', min: 10000, max: 20000 },
    { label: '$20K - $50K', min: 20000, max: 50000 },
    { label: '$50K - $100K', min: 50000, max: 100000 },
    { label: '$100K - $200K', min: 100000, max: 200000 },
    { label: '$200K - $500K', min: 200000, max: 500000 },
    { label: '$500K - $1M', min: 500000, max: 1000000 },
    { label: '$1M - $2M', min: 1000000, max: 2000000 },
    { label: '$2M - $5M', min: 2000000, max: 5000000 },
    { label: '$5M+', min: 5000000, max: Infinity },
  ];
  
  // Get deal size range for a given amount
  const getDealSizeRange = (dealSize) => {
    if (!dealSize) return null;
    const amount = parseFloat(dealSize);
    if (isNaN(amount)) return null;
    const range = dealSizeRanges.find(r => amount >= r.min && amount < r.max);
    return range ? range.label : null;
  };
  
  // Country to continent mapping
  const countryToContinent = {
    // Europe
    'Slovakia': 'Europe',
    'Czech Republic': 'Europe',
    'Czechia': 'Europe',
    'Germany': 'Europe',
    'Austria': 'Europe',
    'Poland': 'Europe',
    'Hungary': 'Europe',
    'France': 'Europe',
    'Italy': 'Europe',
    'Spain': 'Europe',
    'Portugal': 'Europe',
    'Netherlands': 'Europe',
    'Belgium': 'Europe',
    'Switzerland': 'Europe',
    'UK': 'Europe',
    'United Kingdom': 'Europe',
    'Ireland': 'Europe',
    'Sweden': 'Europe',
    'Norway': 'Europe',
    'Denmark': 'Europe',
    'Finland': 'Europe',
    'Greece': 'Europe',
    'Croatia': 'Europe',
    'Slovenia': 'Europe',
    'Romania': 'Europe',
    'Bulgaria': 'Europe',
    'Serbia': 'Europe',
    'Ukraine': 'Europe',
    'Russia': 'Europe',
    // North America
    'USA': 'North America',
    'United States': 'North America',
    'Canada': 'North America',
    'Mexico': 'North America',
    // South America
    'Brazil': 'South America',
    'Argentina': 'South America',
    'Chile': 'South America',
    'Colombia': 'South America',
    'Peru': 'South America',
    // Asia
    'China': 'Asia',
    'Japan': 'Asia',
    'South Korea': 'Asia',
    'India': 'Asia',
    'Singapore': 'Asia',
    'Hong Kong': 'Asia',
    'Taiwan': 'Asia',
    'Thailand': 'Asia',
    'Vietnam': 'Asia',
    'Malaysia': 'Asia',
    'Indonesia': 'Asia',
    'Philippines': 'Asia',
    'UAE': 'Asia',
    'United Arab Emirates': 'Asia',
    'Israel': 'Asia',
    'Saudi Arabia': 'Asia',
    // Africa
    'South Africa': 'Africa',
    'Nigeria': 'Africa',
    'Egypt': 'Africa',
    'Kenya': 'Africa',
    'Morocco': 'Africa',
    // Oceania
    'Australia': 'Oceania',
    'New Zealand': 'Oceania',
  };
  
  // Extract country from location string
  const extractCountry = (location) => {
    if (!location) return null;
    const parts = location.split(',').map(p => p.trim());
    const lastPart = parts[parts.length - 1];
    if (lastPart && lastPart.length > 2 && !/^\d+$/.test(lastPart)) {
      return lastPart;
    }
    return null;
  };
  
  // Get continent from location
  const getContinent = (location) => {
    const country = extractCountry(location);
    if (!country) return null;
    return countryToContinent[country] || 'Other';
  };
  
  const continents = [...new Set(contacts.map(c => getContinent(c.location)).filter(Boolean))].sort();

  // Format currency helper with smart abbreviations (K, M, B)
  const formatCurrency = (amount) => {
    if (!amount) return '';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return amount; // Return as-is if not a number (e.g., "$5M-$10M", ranges)
    
    const absNum = Math.abs(num);
    
    // Billions
    if (absNum >= 1000000000) {
      const billions = num / 1000000000;
      return `$${billions % 1 === 0 ? billions.toFixed(0) : billions.toFixed(1)}B`;
    }
    
    // Millions
    if (absNum >= 1000000) {
      const millions = num / 1000000;
      // Show up to 3 decimals for precision, but remove trailing zeros
      const formatted = millions.toFixed(3).replace(/\.?0+$/, '');
      return `$${formatted}M`;
    }
    
    // Thousands
    if (absNum >= 1000) {
      const thousands = num / 1000;
      const formatted = thousands.toFixed(1).replace(/\.?0+$/, '');
      return `$${formatted}K`;
    }
    
    // Less than 1000, show as regular currency
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  // Filter and sort contacts
  const filteredContacts = useMemo(() => {
    let result = [...contacts];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(contact =>
        contact.firstName.toLowerCase().includes(query) ||
        contact.lastName.toLowerCase().includes(query) ||
        contact.email.toLowerCase().includes(query) ||
        (contact.company && contact.company.toLowerCase().includes(query)) ||
        (contact.phone && contact.phone.includes(query))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(contact => contact.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      result = result.filter(contact => contact.type === typeFilter);
    }

    // Deal size range filter
    if (dealSizeFilter !== 'all') {
      const selectedRange = dealSizeRanges.find(r => r.label === dealSizeFilter);
      if (selectedRange) {
        result = result.filter(contact => {
          const amount = parseFloat(contact.dealSize);
          if (isNaN(amount)) return false;
          return amount >= selectedRange.min && amount < selectedRange.max;
        });
      }
    }

    // Continent filter
    if (locationFilter !== 'all') {
      result = result.filter(contact => {
        const continent = getContinent(contact.location);
        return continent === locationFilter;
      });
    }

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle name sorting - sort by firstName primarily
      if (sortConfig.key === 'lastName') {
        aVal = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
        bVal = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
      }
      // Handle numeric sorting for dealSize
      else if (sortConfig.key === 'dealSize') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } 
      // Handle date sorting for lastContact
      else if (sortConfig.key === 'lastContact') {
        aVal = a.lastContact ? new Date(a.lastContact).getTime() : 0;
        bVal = b.lastContact ? new Date(b.lastContact).getTime() : 0;
      }
      // Handle organization sorting - put linked orgs first when ascending
      else if (sortConfig.key === 'organizationName') {
        // Use organizationName, fall back to company, then empty string
        aVal = a.organizationName || a.company || '';
        bVal = b.organizationName || b.company || '';
        
        // When ascending, contacts WITH organizations come first
        // When descending, contacts WITHOUT organizations come first
        const aHasOrg = !!a.organizationName;
        const bHasOrg = !!b.organizationName;
        
        if (aHasOrg !== bHasOrg) {
          if (sortConfig.direction === 'asc') {
            return aHasOrg ? -1 : 1; // Linked orgs first
          } else {
            return aHasOrg ? 1 : -1; // Unlinked first
          }
        }
        
        // Both have or both don't have org - sort alphabetically
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      else if (typeof aVal === 'string') {
        aVal = (aVal || '').toLowerCase();
      }
      if (typeof bVal === 'string' && !['dealSize', 'organizationName', 'lastContact', 'lastName'].includes(sortConfig.key)) {
        bVal = (bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [contacts, searchQuery, statusFilter, typeFilter, dealSizeFilter, locationFilter, sortConfig]);

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedContacts.length === filteredContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(filteredContacts.map(c => c.id));
    }
  };

  // Handle individual select
  const handleSelectContact = (id) => {
    setSelectedContacts(prev =>
      prev.includes(id)
        ? prev.filter(cId => cId !== id)
        : [...prev, id]
    );
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setTypeFilter('all');
    setDealSizeFilter('all');
    setLocationFilter('all');
  };

  // Check if any filters are active
  const hasActiveFilters = searchQuery || statusFilter !== 'all' || typeFilter !== 'all' || dealSizeFilter !== 'all' || locationFilter !== 'all';

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown size={14} className="ml-1 text-zinc-600" />;
    return sortConfig.direction === 'asc' 
      ? <ChevronUp size={14} className="ml-1 text-blue-400" />
      : <ChevronDown size={14} className="ml-1 text-blue-400" />;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-zinc-100">All Contacts</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {isLoading ? 'Loading...' : (
              <>
                {filteredContacts.length} contact{filteredContacts.length !== 1 ? 's' : ''}
                {hasActiveFilters && ` (filtered from ${contacts.length})`}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="bg-transparent border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white"
          >
            <Download size={16} />
            Export
          </Button>
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={handleAddNewContact}
          >
            <UserPlus size={16} />
            Add Contact
          </Button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-[#0f1115] border border-white/6 rounded-lg p-4 space-y-4">
        {/* Basic Filters Row */}
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-[#0a0a0a] border-white/10 text-zinc-200 placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
            />
          </div>

          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-[150px] bg-[#0a0a0a] border-white/10 text-zinc-300">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f1115] border-white/10">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>

          {/* Type Filter */}
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full md:w-[150px] bg-[#0a0a0a] border-white/10 text-zinc-300">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f1115] border-white/10">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="investor">Investor</SelectItem>
              <SelectItem value="portfolio">Portfolio</SelectItem>
            </SelectContent>
          </Select>

          {/* Advanced Filters Toggle */}
          <Button
            variant="outline"
            size="default"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`bg-transparent border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white ${
              showAdvancedFilters ? 'bg-white/5 text-white border-white/20' : ''
            }`}
          >
            <SlidersHorizontal size={16} />
            Advanced
            {showAdvancedFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Button>
        </div>

        {/* Advanced Filters */}
        <AnimatePresence>
          {showAdvancedFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-visible"
            >
              <div className="pt-3 border-t border-white/6 flex flex-col md:flex-row gap-3">
                {/* Deal Size Filter */}
                <Select value={dealSizeFilter} onValueChange={setDealSizeFilter}>
                  <SelectTrigger className="w-full md:w-[180px] bg-[#0a0a0a] border-white/10 text-zinc-300">
                    <SelectValue placeholder="Deal Size" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f1115] border-white/10">
                    <SelectItem value="all">All Deal Sizes</SelectItem>
                    {dealSizeRanges.map(range => (
                      <SelectItem key={range.label} value={range.label}>{range.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Continent Filter */}
                <Select value={locationFilter} onValueChange={setLocationFilter}>
                  <SelectTrigger className="w-full md:w-[180px] bg-[#0a0a0a] border-white/10 text-zinc-300">
                    <SelectValue placeholder="Continent" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f1115] border-white/10">
                    <SelectItem value="all">All Continents</SelectItem>
                    {continents.map(continent => (
                      <SelectItem key={continent} value={continent}>{continent}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="default"
                    onClick={clearFilters}
                    className="text-zinc-400 hover:text-white hover:bg-white/5"
                  >
                    <X size={16} />
                    Clear Filters
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Active Filters Tags */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 pt-2">
            {searchQuery && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-500/10 text-blue-400 text-xs border border-blue-500/20">
                Search: "{searchQuery}"
                <button onClick={() => setSearchQuery('')} className="hover:text-blue-300">
                  <X size={12} />
                </button>
              </span>
            )}
            {statusFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/10 text-emerald-400 text-xs border border-emerald-500/20">
                Status: {statusFilter}
                <button onClick={() => setStatusFilter('all')} className="hover:text-emerald-300">
                  <X size={12} />
                </button>
              </span>
            )}
            {typeFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-500/10 text-purple-400 text-xs border border-purple-500/20">
                Type: {typeFilter}
                <button onClick={() => setTypeFilter('all')} className="hover:text-purple-300">
                  <X size={12} />
                </button>
              </span>
            )}
            {dealSizeFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500/10 text-amber-400 text-xs border border-amber-500/20">
                Deal: {dealSizeFilter}
                <button onClick={() => setDealSizeFilter('all')} className="hover:text-amber-300">
                  <X size={12} />
                </button>
              </span>
            )}
            {locationFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-sky-500/10 text-sky-400 text-xs border border-sky-500/20">
                Location: {locationFilter}
                <button onClick={() => setLocationFilter('all')} className="hover:text-sky-300">
                  <X size={12} />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      <AnimatePresence>
        {selectedContacts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-blue-500/10 border border-blue-500/20 rounded-lg px-4 py-3 flex items-center justify-between"
          >
            <span className="text-sm text-blue-400">
              {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                Export Selected
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                onClick={handleBulkDelete}
              >
                Delete Selected
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedContacts([])}
                className="text-zinc-400 hover:text-white"
              >
                Clear Selection
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table */}
      <div className="bg-[#0f1115] border border-white/6 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/6 hover:bg-transparent">
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={selectedContacts.length === filteredContacts.length && filteredContacts.length > 0}
                  onCheckedChange={handleSelectAll}
                  className="border-white/20 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                />
              </TableHead>
              <TableHead 
                className="text-zinc-400 font-medium cursor-pointer hover:text-zinc-200 transition-colors"
                onClick={() => handleSort('lastName')}
              >
                <div className="flex items-center">
                  Name
                  <SortIndicator columnKey="lastName" />
                </div>
              </TableHead>
              <TableHead 
                className="text-zinc-400 font-medium cursor-pointer hover:text-zinc-200 transition-colors"
                onClick={() => handleSort('organizationName')}
              >
                <div className="flex items-center">
                  Organization
                  <SortIndicator columnKey="organizationName" />
                </div>
              </TableHead>
              <TableHead 
                className="text-zinc-400 font-medium cursor-pointer hover:text-zinc-200 transition-colors hidden lg:table-cell"
                onClick={() => handleSort('email')}
              >
                <div className="flex items-center">
                  Contact
                  <SortIndicator columnKey="email" />
                </div>
              </TableHead>
              <TableHead 
                className="text-zinc-400 font-medium cursor-pointer hover:text-zinc-200 transition-colors hidden md:table-cell"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center">
                  Status
                  <SortIndicator columnKey="status" />
                </div>
              </TableHead>
              <TableHead 
                className="text-zinc-400 font-medium cursor-pointer hover:text-zinc-200 transition-colors hidden xl:table-cell"
                onClick={() => handleSort('type')}
              >
                <div className="flex items-center">
                  Type
                  <SortIndicator columnKey="type" />
                </div>
              </TableHead>
              <TableHead 
                className="text-zinc-400 font-medium cursor-pointer hover:text-zinc-200 transition-colors hidden xl:table-cell"
                onClick={() => handleSort('dealSize')}
              >
                <div className="flex items-center">
                  Deal Size
                  <SortIndicator columnKey="dealSize" />
                </div>
              </TableHead>
              <TableHead className="text-zinc-400 font-medium hidden xl:table-cell">Tags</TableHead>
              <TableHead 
                className="text-zinc-400 font-medium cursor-pointer hover:text-zinc-200 transition-colors hidden lg:table-cell"
                onClick={() => handleSort('lastContact')}
              >
                <div className="flex items-center">
                  Last Contact
                  <SortIndicator columnKey="lastContact" />
                </div>
              </TableHead>
              <TableHead className="text-zinc-400 font-medium w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredContacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="h-32 text-center">
                  <div className="flex flex-col items-center justify-center text-zinc-500">
                    <Search size={32} className="mb-2 opacity-50" />
                    <p className="text-sm">No contacts found</p>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="text-blue-400 hover:text-blue-300 text-sm mt-1"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredContacts.map((contact) => (
                <TableRow
                  key={contact.id}
                  ref={highlightedContactId === contact.id ? highlightedRowRef : null}
                  className={`border-white/6 transition-all duration-300 ${
                    highlightedContactId === contact.id
                      ? 'bg-blue-500/20 ring-2 ring-blue-500/50 ring-inset animate-pulse'
                      : selectedContacts.includes(contact.id)
                      ? 'bg-blue-500/5'
                      : 'hover:bg-white/[0.02]'
                  }`}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedContacts.includes(contact.id)}
                      onCheckedChange={() => handleSelectContact(contact.id)}
                      className="border-white/20 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-zinc-200">
                        {contact.firstName} {contact.lastName}
                      </p>
                      <p className="text-xs text-zinc-500">{contact.role}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Popover open={linkingContactId === contact.id && orgSearchOpen} onOpenChange={(open) => {
                      if (open) {
                        setLinkingContactId(contact.id);
                        setOrgSearchOpen(true);
                      } else {
                        setOrgSearchOpen(false);
                        setLinkingContactId(null);
                      }
                    }}>
                      <PopoverTrigger asChild>
                        <button className="flex items-center gap-2 text-left hover:bg-white/5 rounded px-2 py-1 -mx-2 -my-1 transition-colors group w-full min-h-[24px]">
                          {contact.organizationName ? (
                            <span className="text-zinc-300 truncate">{contact.organizationName}</span>
                          ) : contact.company ? (
                            <span className="text-zinc-400 truncate">{contact.company}</span>
                          ) : null}
                          <Link2 size={12} className="text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity ml-auto flex-shrink-0" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[250px] p-0 bg-[#0f1115] border-white/10" align="start">
                        <Command className="bg-transparent">
                          <CommandInput placeholder="Search organizations..." className="border-0 focus:ring-0 text-zinc-200 placeholder:text-zinc-500" />
                          <CommandList>
                            <CommandEmpty className="py-4 text-center text-sm text-zinc-500">No organizations found</CommandEmpty>
                            <CommandGroup>
                              {contact.organizationId && (
                                <CommandItem
                                  onSelect={() => handleLinkOrganization(contact.id, null)}
                                  className="text-zinc-400 hover:bg-white/5 cursor-pointer"
                                >
                                  <X size={14} className="mr-2" />
                                  Unlink organization
                                </CommandItem>
                              )}
                              {organizations.map((org) => (
                                <CommandItem
                                  key={org.id}
                                  value={org.name}
                                  onSelect={() => handleLinkOrganization(contact.id, org.id)}
                                  className="text-zinc-300 hover:bg-white/5 cursor-pointer"
                                >
                                  <Building2 size={14} className="mr-2 text-zinc-500" />
                                  {org.name}
                                  {contact.organizationId === org.id && (
                                    <Check size={14} className="ml-auto text-blue-400" />
                                  )}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="space-y-1">
                      <a 
                        href={`mailto:${contact.email}`}
                        className="flex items-center gap-2 text-zinc-400 text-xs hover:text-blue-400 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Mail size={12} />
                        <span className="truncate max-w-[180px]">{contact.email}</span>
                      </a>
                      {contact.phone && (
                        <a 
                          href={`tel:${contact.phone}`}
                          className="flex items-center gap-2 text-zinc-500 text-xs hover:text-blue-400 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Phone size={12} />
                          <span>{contact.phone}</span>
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <StatusBadge status={contact.status} />
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    <TypeBadge type={contact.type} />
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    <span className="text-zinc-400 text-sm">{formatCurrency(contact.dealSize)}</span>
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    <div className="flex items-center gap-1 overflow-hidden">
                      {contact.tags && contact.tags.length > 0 ? (
                        <>
                          {contact.tags.slice(0, 2).map((tag) => (
                            <span
                              key={tag.id}
                              className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border whitespace-nowrap flex-shrink-0"
                              style={{
                                backgroundColor: `${tag.color}15`,
                                color: tag.color,
                                borderColor: `${tag.color}30`
                              }}
                            >
                              {tag.name}
                            </span>
                          ))}
                          {contact.tags.length > 2 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-medium bg-zinc-700/50 text-zinc-400 border border-zinc-600/50 whitespace-nowrap flex-shrink-0">
                              +{contact.tags.length - 2}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-zinc-600 text-xs">No tags</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <button
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey) {
                          handleClearLastContact(contact.id);
                        } else {
                          handleUpdateLastContact(contact.id);
                        }
                      }}
                      className="flex items-center gap-2 text-zinc-500 text-xs hover:text-blue-400 hover:bg-white/5 rounded px-3 py-2 -mx-3 -my-2 transition-colors group w-full min-w-[180px]"
                      title="Click to update to now • Cmd/Ctrl+Click to clear"
                    >
                      <span className="flex-grow">
                        {contact.lastContact 
                          ? new Date(contact.lastContact).toLocaleString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            }) 
                          : 'Set date'}
                      </span>
                      <Clock size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-400 flex-shrink-0" />
                    </button>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-500 hover:text-zinc-200 hover:bg-white/5">
                          <MoreHorizontal size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#0f1115] border-white/10">
                        <DropdownMenuItem 
                          className="text-zinc-300 focus:bg-white/5 focus:text-white cursor-pointer"
                          onClick={() => handleViewContact(contact)}
                        >
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-zinc-300 focus:bg-white/5 focus:text-white cursor-pointer"
                          onClick={() => handleEditContact(contact)}
                        >
                          Edit Contact
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-zinc-300 focus:bg-white/5 focus:text-white cursor-pointer"
                          onClick={() => window.location.href = `mailto:${contact.email}`}
                        >
                          Send Email
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-white/10" />
                        <DropdownMenuItem 
                          className="text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer"
                          onClick={() => handleDeleteContact(contact.id)}
                        >
                          Delete Contact
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Info */}
      <div className="flex items-center justify-between text-sm text-zinc-500">
        <span>
          Showing {filteredContacts.length} of {contacts.length} contacts
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled
            className="bg-transparent border-white/10 text-zinc-500 disabled:opacity-50"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled
            className="bg-transparent border-white/10 text-zinc-500 disabled:opacity-50"
          >
            Next
          </Button>
        </div>
      </div>

      {/* Create/Edit Contact Modal */}
      <CreateContactModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingContact(null);
        }}
        onSubmit={handleCreateContact}
        editContact={editingContact}
      />

      {/* View Contact Modal */}
      <ViewContactModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setViewingContact(null);
        }}
        contact={viewingContact}
        onEdit={handleEditContact}
      />

    </motion.div>
  );
};

export default AllContacts;
